import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChevronRight, Loader2, Pencil, Plus, SortAsc, SortDesc, Trash2, Wallet } from "lucide-react";
import { api, fmtMoney, formatErr } from "@/lib/api";
import { ColonyDotName } from "@/lib/colonyColors";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SortableHeader, makeComparator } from "@/components/SortableHeader";
import { ReceivedPaymentEditDialog } from "@/components/finance/ReceivedPaymentEditDialog";
import { RecordReceivedPaymentDialog } from "@/components/finance/RecordReceivedPaymentDialog";
import { DeletePaymentDialog } from "@/components/finance/DeletePaymentDialog";
import { toast } from "sonner";
import { FinanceSummaryCards } from "./FinanceSummaryCards";
import { TutorialButton } from "@/components/tutorials/TutorialButton";

function paymentMethodLabel(payment) {
  const method = payment.payment_method || "Not specified";
  const reference = payment.payment_reference || "";
  if (!reference) return method;
  return `${method} · ${method.toLowerCase().includes("check") ? "#" : ""}${reference}`;
}

function ReceivedPaymentRow({ payment, onOpenInvoice, onEdit, onDelete, isFocused }) {
  const canOpen = Boolean(payment.invoice_id && payment.invoice_number);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onEdit(payment)}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onEdit(payment);
        }
      }}
      aria-label={`Open payment details for ${payment.colony_name || "colony"}`}
      aria-current={isFocused ? "true" : undefined}
      data-focused={isFocused ? "true" : undefined}
      className={`admin-responsive-row grid grid-cols-12 gap-y-1 px-4 py-3 items-center border-b last:border-b-0 cursor-pointer touch-manipulation transition-[background-color,border-color,box-shadow] duration-300 hover:bg-secondary/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset ${isFocused ? "relative z-[1] border-primary bg-primary/10 ring-2 ring-inset ring-primary" : "border-border"}`}
      data-testid={`received-payment-row-${payment.id}`}
    >
      <div className="col-span-2 text-sm mono-num" data-mobile-label="Date" data-mobile-span="full" data-testid={`received-payment-date-${payment.id}`}>{payment.paid_at || "—"}</div>
      <div className="col-span-2 text-sm font-semibold min-w-0" data-mobile-label="Colony" data-mobile-span="full" data-testid={`received-payment-colony-${payment.id}`}>
        <ColonyDotName id={payment.colony_id} name={payment.colony_name || "Colony"} />
      </div>
      <div className="col-span-2 min-w-0" data-mobile-label="Invoice" data-mobile-span="full" data-testid={`received-payment-invoice-${payment.id}`}>
        {canOpen ? (
          <button
            type="button"
            className="block w-full min-w-0 text-left rounded-sm hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            onPointerDown={(event) => event.stopPropagation()}
            onClick={(event) => { event.stopPropagation(); onOpenInvoice(payment); }}
            aria-label={`Open invoice ${payment.invoice_number}`}
            data-testid={`received-payment-open-invoice-${payment.id}`}
          >
            <div className="flex items-center gap-1.5 min-w-0"><span className="font-semibold text-sm underline underline-offset-2 decoration-border truncate">{payment.invoice_number}</span><span className="text-[9px] px-1 py-0.5 rounded-sm bg-secondary text-muted-foreground font-semibold uppercase tracking-wider shrink-0">{payment.invoice_type === "payroll" ? "Payroll" : "Invoice"}</span></div>
            <div className="text-[10px] text-amber-700 mt-0.5" data-testid={`received-payment-invoice-due-${payment.id}`}>Still due {fmtMoney(payment.invoice_balance_due)}</div>
            {payment.allocation_count > 1 && <div className="text-[10px] text-muted-foreground">Allocation {payment.allocation_index} of {payment.allocation_count}</div>}
          </button>
        ) : <span className="text-sm text-muted-foreground">Manual payment</span>}
      </div>
      <div className="col-span-2 text-sm min-w-0" data-mobile-label="Paid by" data-mobile-span="full" data-testid={`received-payment-method-${payment.id}`}>
        <div className="truncate" title={paymentMethodLabel(payment)}>{paymentMethodLabel(payment)}</div>
      </div>
      <div className="col-span-2 text-xs text-muted-foreground min-w-0" data-mobile-label="Note" data-mobile-span="full" data-testid={`received-payment-note-${payment.id}`}>
        <span className="block truncate" title={payment.note || ""}>{payment.note || "—"}</span>
      </div>
      <div className="col-span-2 flex items-center justify-end gap-2" data-mobile-label="Amount" data-mobile-span="full">
        <span className="mono-num font-bold text-emerald-700" data-testid={`received-payment-amount-${payment.id}`}>{fmtMoney(payment.amount)}</span>
        <Button size="icon" variant="ghost" className="relative z-10 h-9 w-9 shrink-0" onPointerDown={(event) => event.stopPropagation()} onClick={(event) => { event.stopPropagation(); onEdit(payment); }} title="Edit payment" aria-label={`Edit payment for ${payment.colony_name}`} data-testid={`received-payment-edit-${payment.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
        <Button size="icon" variant="ghost" className="relative z-10 h-9 w-9 shrink-0 text-red-600 hover:text-red-700 hover:bg-red-50" onPointerDown={(event) => event.stopPropagation()} onClick={(event) => { event.stopPropagation(); onDelete(payment); }} title="Delete payment" aria-label={`Delete ${fmtMoney(payment.amount)} payment${payment.invoice_number ? ` from ${payment.invoice_number}` : ""}`} data-testid={`received-payment-delete-${payment.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
        {canOpen && <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" aria-hidden="true" />}
      </div>
    </div>
  );
}

export const ReceivedPaymentsPanel = ({ dateFrom, dateTo, onOpenInvoice, focusRequest }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [recordOpen, setRecordOpen] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [sortKey, setSortKey] = useState("date");
  const [sortDir, setSortDir] = useState("desc");
  const [focusedPaymentId, setFocusedPaymentId] = useState(null);
  const handledFocusRequestRef = useRef(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/mgmt/payments", { params: { date_from: dateFrom, date_to: dateTo } })
      .then((response) => setRows(response.data || []))
      .catch((error) => toast.error(formatErr(error.response?.data?.detail) || error.message))
      .finally(() => setLoading(false));
  }, [dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!focusRequest?.invoiceId || loading) return;
    const requestKey = focusRequest.requestId || focusRequest.invoiceId;
    if (handledFocusRequestRef.current === requestKey) return;
    handledFocusRequestRef.current = requestKey;

    const revealPayment = async () => {
      let linkedPayments = rows.filter((row) => row.invoice_id === focusRequest.invoiceId);
      if (!linkedPayments.length) {
        try {
          const response = await api.get("/mgmt/payments", { params: { invoice_id: focusRequest.invoiceId } });
          linkedPayments = response.data || [];
          if (linkedPayments.length) {
            setRows((current) => {
              const knownIds = new Set(current.map((row) => row.id));
              return [...linkedPayments.filter((row) => !knownIds.has(row.id)), ...current];
            });
          }
        } catch (error) {
          toast.error(formatErr(error.response?.data?.detail) || error.message);
          return;
        }
      }
      const target = linkedPayments[0];
      if (!target) {
        toast.error(`No received payment was found for ${focusRequest.invoiceNumber || "this invoice"}.`);
        return;
      }
      setFocusedPaymentId(target.id);
    };

    revealPayment();
  }, [focusRequest, loading, rows]);
  const total = useMemo(() => rows.reduce((sum, row) => sum + Number(row.amount || 0), 0), [rows]);
  const valueForSort = useCallback((payment) => ({
    date: payment.paid_at,
    colony: payment.colony_name,
    invoice: payment.invoice_number || "Manual payment",
    method: paymentMethodLabel(payment),
    note: payment.note,
    amount: Number(payment.amount || 0),
  }[sortKey]), [sortKey]);
  const sortedRows = useMemo(() => [...rows].sort(makeComparator(valueForSort, sortDir)), [rows, sortDir, valueForSort]);
  useEffect(() => {
    if (!focusedPaymentId || !sortedRows.some((row) => row.id === focusedPaymentId)) return;
    const timer = window.setTimeout(() => {
      const target = document.querySelector(`[data-testid="received-payment-row-${focusedPaymentId}"]`);
      target?.scrollIntoView({ behavior: "smooth", block: "center" });
      target?.focus({ preventScroll: true });
    }, 80);
    return () => window.clearTimeout(timer);
  }, [focusedPaymentId, sortedRows]);
  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((direction) => direction === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("asc"); }
  };

  return (
    <section className="management-section space-y-4" aria-labelledby="received-payments-heading" data-testid="received-payments-panel">
      <div className="management-section-header flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2">
        <div>
          <div className="flex flex-wrap items-center gap-3"><h1 id="received-payments-heading" className="management-title font-display text-lg font-semibold" data-testid="received-payments-heading">Received payments</h1><TutorialButton section="payments" compact /></div>
          <p className="text-xs text-muted-foreground" data-testid="received-payments-description">History of money received from bungalow colonies.</p>
        </div>
        <div className="flex items-center gap-3 sm:justify-end">
          <Button size="sm" onClick={() => setRecordOpen(true)} data-testid="received-payments-record-button"><Plus className="w-4 h-4 mr-1.5" />Record</Button>
        </div>
      </div>
      <FinanceSummaryCards testId="received-payment-summary-cards" loading={loading} items={[
        { key: "count", label: "Payments received", value: rows.length, icon: Wallet, valueTestId: "received-payments-count" },
        { key: "total", label: "Total received", value: fmtMoney(total), icon: Wallet, tone: "green", valueTestId: "received-payments-total" },
      ]} />

      <div className="flex lg:hidden items-end gap-2" data-testid="received-payments-mobile-sort">
        <div className="flex-1 min-w-0">
          <label className="text-[10px] uppercase tracking-wider text-muted-foreground" htmlFor="received-payment-sort-select">Sort by</label>
          <Select value={sortKey} onValueChange={setSortKey}>
            <SelectTrigger id="received-payment-sort-select" data-testid="received-payment-sort-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="date">Date</SelectItem><SelectItem value="colony">Colony</SelectItem><SelectItem value="invoice">Invoice</SelectItem><SelectItem value="method">Paid by</SelectItem><SelectItem value="note">Note</SelectItem><SelectItem value="amount">Amount</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button variant="outline" size="icon" onClick={() => setSortDir((direction) => direction === "asc" ? "desc" : "asc")} title={sortDir === "asc" ? "Ascending" : "Descending"} aria-label={sortDir === "asc" ? "Sort ascending" : "Sort descending"} data-testid="received-payment-sort-direction">
          {sortDir === "asc" ? <SortAsc className="w-4 h-4" /> : <SortDesc className="w-4 h-4" />}
        </Button>
      </div>

      <div className="management-list border border-border rounded-sm overflow-hidden" data-testid="received-payments-list">
        <div className="admin-responsive-header grid grid-cols-12 px-4 py-2 text-xs font-mono uppercase tracking-wider text-muted-foreground border-b border-border bg-secondary/40 items-center">
          <div className="col-span-2"><SortableHeader sortKey={sortKey} sortDir={sortDir} columnKey="date" onToggle={toggleSort} testId="received-payment-sort-date">Date</SortableHeader></div>
          <div className="col-span-2"><SortableHeader sortKey={sortKey} sortDir={sortDir} columnKey="colony" onToggle={toggleSort} testId="received-payment-sort-colony">Colony</SortableHeader></div>
          <div className="col-span-2"><SortableHeader sortKey={sortKey} sortDir={sortDir} columnKey="invoice" onToggle={toggleSort} testId="received-payment-sort-invoice">Invoice</SortableHeader></div>
          <div className="col-span-2"><SortableHeader sortKey={sortKey} sortDir={sortDir} columnKey="method" onToggle={toggleSort} testId="received-payment-sort-method">Paid by</SortableHeader></div>
          <div className="col-span-2"><SortableHeader sortKey={sortKey} sortDir={sortDir} columnKey="note" onToggle={toggleSort} testId="received-payment-sort-note">Note</SortableHeader></div>
          <div className="col-span-2"><SortableHeader sortKey={sortKey} sortDir={sortDir} columnKey="amount" onToggle={toggleSort} className="ml-auto" testId="received-payment-sort-amount">Amount</SortableHeader></div>
        </div>
        {loading && <div className="px-4 py-12 flex items-center justify-center text-sm text-muted-foreground" data-testid="received-payments-loading"><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading payments…</div>}
        {!loading && rows.length === 0 && (
          <div className="px-4 py-12 text-center text-sm text-muted-foreground" data-testid="received-payments-empty">
            <Wallet className="w-5 h-5 mx-auto mb-2" /> No payments were received in this period.
          </div>
        )}
        {!loading && sortedRows.map((payment) => <ReceivedPaymentRow key={payment.id} payment={payment} onOpenInvoice={onOpenInvoice} onEdit={setEditing} onDelete={setDeleting} isFocused={payment.id === focusedPaymentId} />)}
      </div>
      <ReceivedPaymentEditDialog payment={editing} onOpenChange={(open) => { if (!open) setEditing(null); }} onSaved={() => { setEditing(null); load(); }} />
      <RecordReceivedPaymentDialog open={recordOpen} onOpenChange={setRecordOpen} onSaved={load} />
      <DeletePaymentDialog payment={deleting} onOpenChange={(open) => { if (!open) setDeleting(null); }} onDeleted={() => { setDeleting(null); load(); }} />
    </section>
  );
};